import { useState, useEffect, useCallback, useRef } from "react";
import type {
  ConnectionStatus,
  AuthChallenge,
  InstallProgress,
  SessionModelState,
  HarnessState,
  ModelInfo,
} from "@/types";

export function useAgent() {
  const rendererUnloadingRef = useRef(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [error, setError] = useState<string | null>(null);
  const [authChallenge, setAuthChallenge] = useState<AuthChallenge | null>(
    null,
  );
  const [isTerminalAuth, setIsTerminalAuth] = useState(false);
  const [installProgress, setInstallProgress] =
    useState<InstallProgress | null>(null);

  // Model state
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);
  const [isRefreshingModels, setIsRefreshingModels] = useState(false);

  // Harness states
  const [harnessStates, setHarnessStates] = useState<HarnessState[]>([]);
  const [hasLoadedHarnessStates, setHasLoadedHarnessStates] = useState(false);

  useEffect(() => {
    const markUnloading = () => {
      rendererUnloadingRef.current = true;
    };

    window.addEventListener("beforeunload", markUnloading);
    window.addEventListener("pagehide", markUnloading);

    return () => {
      rendererUnloadingRef.current = true;
      window.removeEventListener("beforeunload", markUnloading);
      window.removeEventListener("pagehide", markUnloading);
    };
  }, []);

  useEffect(() => {
    const api = window.freshvibe;
    if (!api || typeof api.onConnectionStatus !== "function") {
      const msg = "FreshVibe bridge is unavailable (window.freshvibe)";
      console.error(`[useAgent] ${msg}`);
      setError(msg);
      setStatus("error");
      return;
    }

    const unsub1 = api.onConnectionStatus((newStatus) => {
      // Handle terminal-auth variant: "authenticating:terminal" signals
      // that the agent requires interactive terminal login (e.g. Copilot).
      const rawStatus = newStatus as string;
      console.log(`[useAgent] onConnectionStatus: "${rawStatus}"`);
      if (rawStatus === "authenticating:terminal") {
        console.log("[useAgent] → setting isTerminalAuth=true");
        setStatus("authenticating");
        setError(null);
        setIsTerminalAuth(true);
      } else {
        setStatus(rawStatus as ConnectionStatus);
        if (rawStatus !== "installing") {
          setInstallProgress(null);
        }
        if (rawStatus === "authenticating") {
          setError(null);
        }
        if (rawStatus !== "authenticating") {
          console.log(`[useAgent] → clearing isTerminalAuth (status="${rawStatus}")`);
          setIsTerminalAuth(false);
          setAuthChallenge(null);
        }
      }
      if (rawStatus === "ready") {
        setAuthChallenge(null);
        setError(null);
        // Refresh harness states when agent connects
        refreshHarnessStates();
        // Pull the latest model list on connect (Gemini ACP doesn't emit it).
        refreshModels()
          .catch(() => {
            // Best-effort only
          })
          .finally(() => {
            // Re-apply persisted model selection — the agent may have started
            // with its default model, but the user previously chose a different one.
            restoreSavedModel();
          });
      }
      if (rawStatus === "disconnected") {
        setAvailableModels([]);
        setCurrentModelId(null);
      }
    });

    const unsub2 = api.onAuthChallenge((data) => {
      if (!data) {
        setAuthChallenge(null);
        return;
      }
      const challenge = data as AuthChallenge;
      console.log(`[useAgent] onAuthChallenge: url=${challenge.url} code=${challenge.code || "none"}`);
      setAuthChallenge(challenge);
      setError(null);
      // Only set status if not already authenticating — avoids clearing
      // isTerminalAuth when the challenge arrives during terminal-auth flow.
      setStatus((prev) => {
        console.log(`[useAgent] → setStatus prev="${prev}" (keeping as-is if already authenticating)`);
        return prev === "authenticating" ? prev : "authenticating";
      });
    });

    const unsub3 = api.onInstallProgress((data) => {
      const progress = data as InstallProgress;
      if (progress.step === "complete") {
        setInstallProgress(null);
        return;
      }
      setInstallProgress(progress);
      if (progress.step === "error") {
        setError(progress.message);
      }
    });

    const unsub4 = api.onModelsAvailable((data) => {
      const state = data as SessionModelState;
      if (state?.availableModels) {
        setAvailableModels(state.availableModels);
        setCurrentModelId(state.currentModelId);
      }
    });

    // Restore selected agent in UI without auto-connecting on startup.
    // Connection should only begin when the user explicitly reconnects/selects.
    api.getSettings()
      .then((settings) => {
        if (typeof settings.selectedAgent === "string" && settings.selectedAgent) {
          setSelectedAgent(settings.selectedAgent);
        }
        const hasWorkspaceHint = typeof settings.workspaceProvider === "string"
          && settings.workspaceProvider.trim().length > 0
          && typeof settings.workspaceId === "string"
          && settings.workspaceId.trim().length > 0;
        if (hasWorkspaceHint) {
          refreshHarnessStates();
        } else {
          setHasLoadedHarnessStates(true);
        }
      })
      .catch(() => {
        // Best-effort only
        setHasLoadedHarnessStates(true);
      });

    return () => { unsub1?.(); unsub2?.(); unsub3?.(); unsub4?.(); };
  }, []);

  const refreshHarnessStates = useCallback(async () => {
    try {
      const api = window.freshvibe;
      if (!api) return;
      const states = await api.getHarnessStates();
      setHarnessStates(states);
    } catch {
      // Harness check may fail before main process is ready
    } finally {
      setHasLoadedHarnessStates(true);
    }
  }, []);

  const refreshModels = useCallback(async () => {
    try {
      const api = window.freshvibe;
      if (!api) return null;
      setIsRefreshingModels(true);
      const models = await api.refreshModels();
      if (models?.availableModels) {
        setAvailableModels(models.availableModels);
        setCurrentModelId(models.currentModelId);
      }
      return models;
    } catch {
      return null;
    } finally {
      setIsRefreshingModels(false);
    }
  }, []);

  /** Re-apply the user's persisted model if it differs from the agent's default. */
  const restoreSavedModel = useCallback(async () => {
    try {
      const api = window.freshvibe;
      if (!api) return;

      const settings = await api.getSettings();
      const savedModel = settings.selectedModel;
      if (!savedModel) return;

      // Give a brief delay for the agent's model state to arrive via
      // onModelsAvailable before we check and possibly override it.
      await new Promise((r) => setTimeout(r, 500));

      const models = await api.getAvailableModels();
      if (!models) return;

      // Only re-apply if the saved model is available and differs from current
      const isAvailable = models.availableModels.some((m) => m.modelId === savedModel);
      if (isAvailable && savedModel !== models.currentModelId) {
        console.log(`[useAgent] Restoring saved model: ${savedModel} (agent default: ${models.currentModelId})`);
        const success = await api.setModel(savedModel);
        if (success) {
          setCurrentModelId(savedModel);
        }
      }
    } catch {
      // Model restore is best-effort — don't block anything
    }
  }, []);

  const selectAgent = useCallback(async (presetId: string) => {
    setSelectedAgent(presetId);
    setStatus("connecting");
    setError(null);
    setAvailableModels([]);
    setCurrentModelId(null);
    setIsRefreshingModels(false);
    try {
      const api = window.freshvibe;
      if (!api) throw new Error("FreshVibe bridge is unavailable");
      await api.selectAgent(presetId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const invokeAbortedDuringTeardown = msg.includes("reply was never sent")
        && rendererUnloadingRef.current;

      if (invokeAbortedDuringTeardown) {
        console.log(`[useAgent] Ignoring selectAgent teardown during renderer unload: ${msg}`);
        return;
      }

      console.error(`[useAgent] selectAgent("${presetId}") failed:`, msg);
      setError(msg);
      setStatus("error");
    }
  }, []);

  const installAgent = useCallback(async (presetId: string) => {
    setInstallProgress(null);
    try {
      const api = window.freshvibe;
      if (!api) throw new Error("FreshVibe bridge is unavailable");
      const result = await api.installAgent(presetId);
      // Refresh harness states after install
      await refreshHarnessStates();
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      return false;
    }
  }, [refreshHarnessStates]);

  const setModel = useCallback(async (modelId: string) => {
    try {
      const api = window.freshvibe;
      if (!api) return false;
      const success = await api.setModel(modelId);
      if (success) {
        setCurrentModelId(modelId);
        await api.saveSettings("selectedModel", modelId);
      }
      return success;
    } catch {
      return false;
    }
  }, []);

  const stopAgent = useCallback(async () => {
    try {
      const api = window.freshvibe;
      if (!api) return;
      await api.stopAgent();
      setStatus("disconnected");
      setSelectedAgent(null);
      setAvailableModels([]);
      setCurrentModelId(null);
      setIsRefreshingModels(false);
      await refreshHarnessStates();
    } catch {
      // Ignore stop failures
    }
  }, [refreshHarnessStates]);

  const logoutAgent = useCallback(async () => {
    try {
      const api = window.freshvibe;
      if (!api) return;
      await api.logoutAgent();
      setStatus("disconnected");
      setSelectedAgent(null);
      setAvailableModels([]);
      setCurrentModelId(null);
      setIsRefreshingModels(false);
      await refreshHarnessStates();
    } catch {
      // Ignore logout failures
    }
  }, [refreshHarnessStates]);

  const respondToAuth = useCallback(
    async (id: string, result: string) => {
      const api = window.freshvibe;
      if (!api) return;
      await api.respondToAuth(id, result);
      setAuthChallenge(null);
    },
    [],
  );

  const dismissAuthChallenge = useCallback(() => {
    setAuthChallenge(null);
  }, []);

  /** Update the displayed agent ID without starting a connection. */
  const setSelectedAgentId = useCallback((agentId: string | null) => {
    setSelectedAgent(agentId);
  }, []);

  /** Immediately show "connecting" status for instant visual feedback. */
  const setConnectingStatus = useCallback(() => {
    setStatus("connecting");
    setError(null);
  }, []);

  return {
    selectedAgent,
    status,
    error,
    authChallenge,
    isTerminalAuth,
    installProgress,
    availableModels,
    currentModelId,
    isRefreshingModels,
    harnessStates,
    hasLoadedHarnessStates,
    selectAgent,
    installAgent,
    refreshModels,
    setModel,
    stopAgent,
    logoutAgent,
    respondToAuth,
    dismissAuthChallenge,
    refreshHarnessStates,
    setSelectedAgentId,
    setConnectingStatus,
  };
}
